import React, { useMemo } from 'react';
import { Box, Typography, Tooltip, useTheme } from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

/**
 * OutageProbabilityChart
 * Visualizes the probability of surviving an outage of a certain duration.
 * 
 * @param {Object} props
 * @param {Array} props.data - Array of { hours: number, probability: number }
 * @param {number} props.maxHours - X-axis limit (default 24)
 */
export function OutageProbabilityChart({ data, maxHours = 24 }) {
    const theme = useTheme();

    const chartData = useMemo(() => {
        if (!data) return [];
        // Filter to show interesting range, or just passed data
        return data.filter(d => d.hours <= maxHours);
    }, [data, maxHours]);

    if (!data || data.length === 0) {
        return <Typography variant="caption">No probability data available</Typography>;
    }

    return (
        <Box sx={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
                <AreaChart
                    data={chartData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
                >
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis
                        dataKey="hours"
                        label={{ value: 'Outage Duration (Hours)', position: 'insideBottom', offset: -5 }}
                        tick={{ fontSize: 12 }}
                    />
                    <YAxis
                        label={{ value: 'Probability (%)', angle: -90, position: 'insideLeft' }}
                        domain={[0, 100]}
                        tick={{ fontSize: 12 }}
                    />
                    <RechartsTooltip
                        formatter={(value) => [`${value}%`, 'Probability']}
                        labelFormatter={(label) => `${label} Hours`}
                        contentStyle={{ backgroundColor: theme.palette.background.paper, borderRadius: 8 }}
                    />
                    <Area
                        type="monotone"
                        dataKey="probability"
                        stroke={theme.palette.primary.main}
                        fill={theme.palette.primary.light}
                        fillOpacity={0.3}
                    />
                    {/* Add reference lines for standard durations */}
                    <ReferenceLine x={4} stroke="red" strokeDasharray="3 3" label="4h" />
                    <ReferenceLine x={8} stroke="orange" strokeDasharray="3 3" label="8h" />
                    <ReferenceLine x={24} stroke="green" strokeDasharray="3 3" label="24h" />
                </AreaChart>
            </ResponsiveContainer>
        </Box>
    );
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * OutageHeatmap
 * 12x24 Grid showing average survival hours for each Month-Hour slot.
 * 
 * @param {Object} props
 * @param {Array} props.survivalHours - Flat list of 8760 survival hour values
 */
export function OutageHeatmap({ survivalHours }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Aggregation Logic: 8760 -> 12x24 grid
    const heatmapData = useMemo(() => {
        if (!survivalHours || survivalHours.length !== 8760) return null;

        // Initialize 12 months x 24 hours grid
        // Each cell will store [sum, count] to calculate average
        const grid = Array(12).fill().map(() => Array(24).fill().map(() => ({ sum: 0, count: 0 })));

        // Iterate through 8760 hours
        // Assumption: Data starts Jan 1st 00:00. 
        // We know standard year has 8760 hours.
        // We need to map linear index i -> (Month, Hour)

        // Days per month (Scanning a standard non-leap year for simplicity or consistent mapping)
        const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

        let currentMonth = 0;
        let currentDayInMonth = 0;
        let currentHour = 0;

        for (let i = 0; i < 8760; i++) {
            const val = survivalHours[i];

            grid[currentMonth][currentHour].sum += val;
            grid[currentMonth][currentHour].count += 1;

            // Increment time
            currentHour++;
            if (currentHour >= 24) {
                currentHour = 0;
                currentDayInMonth++;
                if (currentDayInMonth >= daysInMonth[currentMonth]) {
                    currentDayInMonth = 0;
                    currentMonth++;
                    // Basic bounds check
                    if (currentMonth > 11) currentMonth = 11;
                }
            }
        }

        // Calculate averages
        return grid.map(monthRow =>
            monthRow.map(cell => cell.count > 0 ? cell.sum / cell.count : 0)
        );

    }, [survivalHours]);

    // Compute adaptive scale from actual data range (must be before early return to satisfy hooks rules)
    const maxAvgHours = useMemo(() => {
        if (!heatmapData) return 24;
        let max = 0;
        heatmapData.forEach(row => row.forEach(val => { if (val > max) max = val; }));
        // Round up to next nice ceiling
        if (max <= 1) return 1;
        if (max <= 2) return 2;
        if (max <= 4) return 4;
        if (max <= 6) return 6;
        if (max <= 8) return 10;
        if (max <= 12) return 15;
        if (max <= 24) return 24;
        if (max <= 48) return 48;
        return Math.ceil(max / 24) * 24;
    }, [heatmapData]);

    if (!heatmapData) {
        return <Typography variant="caption">No hourly simulation data available</Typography>;
    }

    // Color Scale Logic — adaptive to actual data range
    const getColor = (avgHours) => {
        if (maxAvgHours === 0) return isDark ? '#ef5350' : '#d32f2f';
        const ratio = avgHours / maxAvgHours;
        if (ratio < 0.2) return isDark ? '#ef5350' : '#d32f2f'; // Red
        if (ratio < 0.4) return isDark ? '#ff9800' : '#ed6c02'; // Orange
        if (ratio < 0.6) return isDark ? '#ffca28' : '#ffb300'; // Amber
        if (ratio < 0.8) return isDark ? '#9ccc65' : '#7cb342'; // Light Green
        return isDark ? '#66bb6a' : '#2e7d32'; // Green
    };

    const cellWidth = 100 / 24;
    const borderColor = isDark ? 'rgba(255, 255, 255, 0.12)' : '#e0e0e0';

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', border: `1px solid ${borderColor}` }}>
                {/* Hour Labels */}
                <Box sx={{ display: 'flex' }}>
                    <Box sx={{ width: 40 }} />
                    {Array.from({ length: 24 }, (_, h) => (
                        <Box key={h} sx={{ width: `${cellWidth}%`, textAlign: 'center', fontSize: '0.65rem' }}>
                            {h % 3 === 0 ? h : ''}
                        </Box>
                    ))}
                </Box>
                {/* Grid */}
                {heatmapData.map((monthRow, mIdx) => (
                    <Box key={mIdx} sx={{ display: 'flex', borderTop: `1px solid ${borderColor}` }}>
                        <Box sx={{ width: 40, fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRight: `1px solid ${borderColor}` }}>
                            {MONTHS[mIdx]}
                        </Box>
                        {monthRow.map((avgHours, hIdx) => (
                            <Tooltip key={hIdx} title={`${MONTHS[mIdx]} ${hIdx}:00 - Avg Survival: ${avgHours.toFixed(1)} hrs`}>
                                <Box sx={{
                                    width: `${cellWidth}%`,
                                    height: 20,
                                    bgcolor: getColor(avgHours),
                                    opacity: 0.8,
                                    '&:hover': { opacity: 1 }
                                }} />
                            </Tooltip>
                        ))}
                    </Box>
                ))}
            </Box>
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, color: 'text.secondary' }}>
                Seasonal Avg. Survival Hours (Red &lt; {Math.round(maxAvgHours * 0.2)}h, Green &gt; {Math.round(maxAvgHours * 0.8)}h, Max: {maxAvgHours}h)
            </Typography>
        </Box>
    );
}
